// piano-frontend/src/components/ScheduleCarryoverDialog.js
// 스케줄제 진입(신규 등록·유형 전환) 시 남은 티켓 처리 방법을 관리자가 선택하는 다이얼로그.
// 스케줄제는 유효기간이 있는 배치에서만 차감되므로, 백엔드가 409(requiresScheduleCarryover)로
// 내려준 carryover({ ticket, reschedule })를 받아 두 가지 중 선택:
//   ① 유효기간을 정해 이월 → 남은 티켓 전량을 해당 날짜까지 유효한 배치로 생성
//   ② 이월하지 않음 → 0장으로 시작 (이후 '결제 처리'로 새로 발급)
import React, { useEffect, useState } from "react";
import {
  Box, Typography, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Alert, Radio, RadioGroup, FormControlLabel, Chip, CircularProgress
} from "@mui/material";
import Dialog from "./AppDialog";
import dayjs from "dayjs";
import DateField from "./DateField";

const QUICK_MONTHS = [1, 2, 3];

// active: keep-alive 페이지가 숨겨질 때 함께 숨기기 위한 표시용 플래그 (MakeupDecisionDialog와 동일)
export default function ScheduleCarryoverDialog({ open, active = true, memberName, carryover, isUpdate, onCancel, onConfirm, submitting = false }) {
  const [mode, setMode] = useState("CARRY");
  const [expiresAt, setExpiresAt] = useState("");

  useEffect(() => {
    if (open) {
      setMode("CARRY");
      setExpiresAt("");
    }
  }, [open, carryover]);

  if (!carryover) return null;

  const ticket = carryover.ticket || 0;
  const reschedule = carryover.reschedule || 0;
  const today = dayjs().format("YYYY-MM-DD");
  const canConfirm = mode === "DISCARD" || (!!expiresAt && expiresAt >= today);

  const handleConfirm = () => {
    onConfirm(mode === "CARRY" ? { mode: "CARRY", expires_at: expiresAt } : { mode: "DISCARD" });
  };

  return (
    <Dialog open={open && active} onClose={submitting ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isUpdate ? "스케줄제 전환 - 남은 티켓 처리" : "스케줄제 등록 - 초기 티켓 처리"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            <strong>{memberName}</strong>님은 {isUpdate ? "남은" : "입력한"} 티켓이 <strong>{ticket}장</strong>
            {reschedule > 0 && ` (일반 ${Math.max(0, ticket - reschedule)} + 변경권 ${reschedule})`} 있습니다.<br />
            스케줄제는 유효기간이 있는 티켓만 쓸 수 있어 처리 방법을 정해야 합니다.
          </Alert>

          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel
              value="CARRY"
              control={<Radio />}
              sx={{ alignItems: "flex-start", mr: 0, "& .MuiRadio-root": { pt: 0.5 } }}
              label={
                <Box sx={{ py: 0.5 }}>
                  <Typography fontWeight={600}>유효기간을 정해 이월</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ticket}장을 그대로 유지하고, 지정한 날짜까지 사용할 수 있게 합니다.
                  </Typography>
                </Box>
              }
            />
            {mode === "CARRY" && (
              <Stack spacing={1} sx={{ pl: 4, pb: 1.5 }}>
                <DateField
                  label="티켓 유효기간"
                  value={expiresAt}
                  onChange={setExpiresAt}
                  fullWidth
                  disablePast
                  disabled={submitting}
                  helperText="이 날짜까지 티켓을 사용할 수 있습니다."
                />
                <Stack direction="row" spacing={1}>
                  {QUICK_MONTHS.map((m) => (
                    <Chip
                      key={m}
                      size="small"
                      variant="outlined"
                      label={`+${m}개월`}
                      disabled={submitting}
                      onClick={() => setExpiresAt(dayjs().add(m, "month").format("YYYY-MM-DD"))}
                    />
                  ))}
                </Stack>
              </Stack>
            )}
            <FormControlLabel
              value="DISCARD"
              control={<Radio />}
              sx={{ alignItems: "flex-start", mr: 0, "& .MuiRadio-root": { pt: 0.5 } }}
              label={
                <Box sx={{ py: 0.5 }}>
                  <Typography fontWeight={600}>이월하지 않음</Typography>
                  <Typography variant="caption" color="error.main">
                    {ticket}장을 소멸시키고 0장으로 시작합니다. 이후 &lsquo;결제 처리&rsquo;로 새로 발급하세요.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>취소</Button>
        <Button
          variant="contained"
          disabled={!canConfirm || submitting}
          onClick={handleConfirm}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? "적용 중..." : "적용하고 저장"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
